import React, { useState } from 'react';
import { useMercyStatus } from '../hooks/useMercyStatus';
import './MercyBanner.css';

function fmtTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const CHANNEL_LABEL = {
  safeword: 'their safeword',
  combo: 'the emergency key combo',
  manual: 'the desktop app'
};

/**
 * MercyBanner — the single most important alert in the frontend. When the
 * desktop user activates the safeword, ALL enforcement is frozen and this
 * strip stays up, loud, until the partner acknowledges it. Acknowledging only
 * dismisses the loud alert — it does NOT (and cannot) resume enforcement; only
 * the desktop user can do that.
 */
const MercyBanner = () => {
  const { status, feature, hasPeer } = useMercyStatus();
  const [ackBusy, setAckBusy] = useState(false);

  if (!hasPeer || !status || !status.active) return null;

  const acknowledged = !!status.acknowledgedAt;
  const via = CHANNEL_LABEL[status.channel] || 'the mercy handle';

  const onAcknowledge = async () => {
    if (!feature || ackBusy) return;
    setAckBusy(true);
    try { await feature.acknowledge(); } catch { /* status push will correct */ }
    finally { setAckBusy(false); }
  };

  if (acknowledged) {
    return (
      <div className="mercy-banner mercy-banner-muted" role="status">
        <span className="mercy-banner-dot" aria-hidden="true" />
        <span>
          Enforcement is frozen — the desktop user activated the mercy handle
          {status.activatedAt ? ` at ${fmtTime(status.activatedAt)}` : ''}. It stays
          frozen until they choose to resume. You cannot resume it for them.
        </span>
      </div>
    );
  }

  return (
    <div className="mercy-banner mercy-banner-critical" role="alert" aria-live="assertive">
      <div className="mercy-banner-body">
        <span className="mercy-banner-glyph" aria-hidden="true">🕊️</span>
        <div className="mercy-banner-text">
          <strong>Mercy handle activated.</strong> The desktop user stopped everything
          with {via}{status.activatedAt ? ` at ${fmtTime(status.activatedAt)}` : ''}.
          All enforcement is frozen — lockouts, sabotage, scheduled punishments, and
          physical devices. Only they can resume it; nothing you do here can.
        </div>
      </div>
      <button className="mercy-banner-ack" onClick={onAcknowledge} disabled={ackBusy}>
        {ackBusy ? 'Acknowledging…' : 'I understand'}
      </button>
    </div>
  );
};

export default MercyBanner;
